import {useEffect, useMemo, useState} from 'react'
import {useHistory, useLocation} from 'react-router-dom'
import {KTSVG} from '../../../_metronic/helpers'
import {useCategoriesMembers} from '../../hooks/members/useCategoriesMembers'
import {useInfiniteMembersSearch} from '../../hooks/members/useInfiniteMembersSearch'
import {parseResultsSearch} from '../search/resultsParams'
import {searchByType} from '../../services/searchService'
import apiClient from '../../helpers/apiClient'
import {MemberSearchList} from './components/MemberSearchList'
import {MembersCategoriesList} from './components/MembersCategoriesList'
import {useSearchableMembersContext} from '../../context/SearchableMembersContext'

// Maximum members shown under "Other Members" (the preview grid below the matched results).
// Enforced by the backend `?limit=…` so the payload stays small; we still leave a small buffer
// on the client to filter out any match that also appears in the recent-first listing.
const OTHERS_LIMIT = 20
const OTHERS_BUFFER = 5

const asList = (data: any): any[] => {
  if (Array.isArray(data)) return data
  return Array.isArray(data?.results) ? data.results : []
}

// Hidrate specific matches by id — leverages the new `?ids=` filter on UserSearchView. Empty
// input returns an empty array without hitting the wire. PR-I-79: cuando hay categoría activa
// además de query, la matcheamos con `?category=` para no mostrar hits de otras categorías.
const fetchMembersByIds = async (ids: string[], category?: string): Promise<any[]> => {
  if (ids.length === 0) return []
  const params: Record<string, string | number> = {ids: ids.join(','), limit: ids.length}
  if (category) params.category = category
  const {data} = await apiClient.get('/users/search/', {params})
  return asList(data)
}

// Fetch a curated preview for "Other Members" — cap capped at 25 rows so a couple of duplicated
// matches can be filtered client-side without shrinking the section below OTHERS_LIMIT.
const fetchMembersPreview = async (category?: string): Promise<any[]> => {
  const params: Record<string, string | number> = {
    where: '',
    key: '',
    limit: OTHERS_LIMIT + OTHERS_BUFFER,
  }
  if (category) params.category = category
  const {data} = await apiClient.get('/users/search/', {params})
  return asList(data)
}

// Members are shown in a 4-column grid, so paginate in multiples of 4 (5 rows × 4 = 20) so the
// last row always fills up when OTHERS_LIMIT is exhausted.
const PER_PAGE = 20

// Header style shared by the two subsection labels ("Results for …", "Other members"). Kept
// inline so we don't grow the sass surface for one page; matches the family and weight of the
// main Members title but at a smaller size and darker weight so it reads as a subheading.
const subheadingStyle: React.CSSProperties = {
  fontFamily: "'Oswald', sans-serif",
  fontSize: 20,
  fontWeight: 500,
  color: '#151515',
  margin: '8px 0 18px',
  letterSpacing: 0.4,
  textTransform: 'uppercase',
}

const MembersSearchPage = () => {
  const location = useLocation()
  const history = useHistory()
  const {data: categories} = useCategoriesMembers()
  // PR-I-79: la categoría seleccionada vive en SearchableMembersContext (la escribe
  // <MembersCategoriesList/> con setSearchParams). La leemos para pasarla al hook
  // de infinite scroll y a las llamadas del modo búsqueda.
  const {searchParams: searchableParams} = useSearchableMembersContext()
  // PR-I-79: el context deja `category` como number en algunos flujos (chip)
  // y como string vacío en el reset. Normalizamos a string para el resto de la
  // página (comparaciones .length, params de axios, etc.).
  const activeCategoryRaw = (searchableParams as any).category
  const activeCategory =
    activeCategoryRaw == null || activeCategoryRaw === '' ? '' : String(activeCategoryRaw)

  // Click en el título "Members" -> vuelve a /members-search sin query, lo que
  // limpia los filtros aplicados y restablece el modo discovery (infinite scroll).
  // Si ya estás en la ruta sin filtros, es idempotente (react-router hace un
  // replace equivalente y el useEffect vuelve a correr, forzando el reset del hook).
  const resetToDiscovery = () => {
    if (location.pathname === '/members-search' && !location.search) {
      // Idempotente: forzamos un replace para disparar el efecto y limpiar el hook.
      history.replace('/members-search')
    } else {
      history.push('/members-search')
    }
  }

  const [allMembers, setAllMembers] = useState<any[]>([])
  const [matches, setMatches] = useState<any[]>([])
  const [others, setOthers] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [page, setPage] = useState(1)

  const {q} = parseResultsSearch(location.search)
  const trimmed = (q ?? '').trim()
  const hasQuery = trimmed.length > 0

  // Modo "discovery": cuando el usuario entra a /members-search sin escribir
  // nada y sin categoría activa, el listado se va cargando conforme hace scroll
  // (infinite scroll). Con query o con categoría activa, seguimos con el flujo
  // matches + preview + paginación cliente (mismo layout que resultados de búsqueda).
  const hasCategory = activeCategory.length > 0
  const infinite = useInfiniteMembersSearch({
    enabled: !hasQuery && !hasCategory,
    pageSize: PER_PAGE,
  })

  // PR-I-79: nombre legible de la categoría activa (para el heading
  // "Results for <Categoría>"). Cae al id si aún no cargaron los catálogos.
  const activeCategoryName = useMemo(() => {
    if (!hasCategory) return ''
    const list = Array.isArray(categories) ? categories : []
    const hit = list.find((c: any) => String(c?.id) === String(activeCategory))
    return hit?.name ?? activeCategory
  }, [categories, activeCategory, hasCategory])

  useEffect(() => {
    const container = document.getElementById('kt_content_container')
    container?.classList.add('member-full-wrapper')
    return () => container?.classList.remove('member-full-wrapper')
  }, [])

  useEffect(() => {
    // Sin query y sin categoría: el hook de infinite scroll maneja la carga;
    // limpiamos el estado de matches/others para no mezclar visualmente el
    // listado paginado con el que administra el hook.
    if (!hasQuery && !hasCategory) {
      setMatches([])
      setOthers([])
      setAllMembers([])
      setPage(1)
      setLoading(false)
      return
    }

    let cancelled = false
    ;(async () => {
      setLoading(true)
      try {
        // Con query: matches por texto (aggregator), luego hidrata por ids + preview.
        // Sin query pero con categoría: matches = preview filtrado por category,
        // others = preview sin filtro (excluyendo los que ya salieron arriba).
        if (hasQuery) {
          const res = await searchByType('members', trimmed, 100, 0)
          const matchIds: string[] = (res.results ?? [])
            .map((r) => String((r as any)?.id ?? ''))
            .filter(Boolean)

          const [matchesData, previewData] = await Promise.all([
            fetchMembersByIds(matchIds, activeCategory),
            fetchMembersPreview(activeCategory),
          ])

          const matchesById = new Map(matchesData.map((m) => [String(m.id), m]))
          const orderedMatches = matchIds
            .map((id) => matchesById.get(id))
            .filter((x): x is any => Boolean(x))

          const matchIdSet = new Set(orderedMatches.map((m) => String(m.id)))
          const rest = previewData
            .filter((m) => !matchIdSet.has(String(m.id)))
            .slice(0, OTHERS_LIMIT)

          if (cancelled) return
          setAllMembers(previewData)
          setMatches(orderedMatches)
          setOthers(rest)
          setPage(1)
          return
        }

        // Solo categoría activa (sin query): traemos en paralelo los que tienen
        // la categoría asignada y una preview sin filtro para "Other members".
        const [categoryMatches, everyoneElse] = await Promise.all([
          fetchMembersPreview(activeCategory),
          fetchMembersPreview(''),
        ])

        const matchIdSet = new Set(categoryMatches.map((m) => String(m.id)))
        const rest = everyoneElse
          .filter((m) => !matchIdSet.has(String(m.id)))
          .slice(0, OTHERS_LIMIT)

        if (cancelled) return
        setAllMembers(everyoneElse)
        setMatches(categoryMatches)
        setOthers(rest)
        setPage(1)
      } finally {
        if (!cancelled) setLoading(false)
      }
    })()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, hasQuery, hasCategory, activeCategory])

  // Pagination applies only to the "Other members" section (the results block is expected to
  // be small; showing it all avoids paging through 1–2 hits).
  const totalPages = Math.max(1, Math.ceil(others.length / PER_PAGE))
  const paginatedOthers = useMemo(
    () => others.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [others, page]
  )

  const goTo = (p: number) => {
    if (p < 1 || p > totalPages) return
    setPage(p)
    window.scrollTo({top: 0, behavior: 'smooth'})
  }

  const hasMatches = matches.length > 0
  // PR-I-79: en modo categoría-sin-query, mostramos el mismo layout que
  // los resultados de búsqueda (Results for … + Other members).
  const showResultsHeader = hasQuery || hasCategory
  const showOthersHeader = (hasQuery || hasCategory) && others.length > 0
  const resultsHeaderLabel = hasQuery ? `“${trimmed}”` : activeCategoryName

  return (
    <div className='members-page members-search-page'>
      <div className='members-page__header' style={{marginTop: 32}}>
        <h1
          className='members-page__title'
          style={{cursor: 'pointer'}}
          role='link'
          tabIndex={0}
          onClick={resetToDiscovery}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault()
              resetToDiscovery()
            }
          }}
        >
          Members
        </h1>
      </div>

      <MembersCategoriesList data={categories} />

      {showResultsHeader && (
        <h2 style={subheadingStyle}>Results for {resultsHeaderLabel}</h2>
      )}

      {hasMatches && <MemberSearchList data={matches} loading={false} />}

      {hasQuery && !hasMatches && !loading && (
        <div style={{padding: '32px 0 8px', color: '#666', fontSize: 16, textAlign: 'center'}}>
          No members match &ldquo;{trimmed}&rdquo;.
        </div>
      )}

      {hasCategory && !hasQuery && !hasMatches && !loading && (
        <div style={{padding: '32px 0 8px', color: '#666', fontSize: 16, textAlign: 'center'}}>
          No members in {activeCategoryName} yet.
        </div>
      )}

      {/* Modo sin query y sin categoría: listado con infinite scroll silencioso.
          Con categoría activa cambiamos al layout Results/Other members (abajo). */}
      {!hasQuery && !hasCategory && (
        <>
          <MemberSearchList data={infinite.items} loading={infinite.loading && infinite.items.length === 0} />
          <div ref={infinite.sentinelRef} aria-hidden='true' style={{height: 1}} />
        </>
      )}

      {/* Modo con query o con categoría: preview paginada (client-side). */}
      {(hasQuery || hasCategory) && others.length > 0 && (
        <>
          {showOthersHeader && (
            <h2 style={{...subheadingStyle, marginTop: 32}}>Other members</h2>
          )}
          <MemberSearchList data={paginatedOthers} loading={loading && !hasMatches} />
        </>
      )}

      {(hasQuery || hasCategory) && totalPages > 1 && (
        <nav className='members-search-pagination' aria-label='Pagination'>
          <button
            type='button'
            className='members-search-pagination__nav'
            onClick={() => goTo(page - 1)}
            disabled={page === 1}
            aria-label='Previous page'
          >
            ‹
          </button>
          {Array.from({length: totalPages}).map((_, i) => {
            const p = i + 1
            return (
              <button
                key={p}
                type='button'
                className={`members-search-pagination__page ${
                  p === page ? 'members-search-pagination__page--active' : ''
                }`}
                onClick={() => goTo(p)}
                aria-current={p === page ? 'page' : undefined}
              >
                {p}
              </button>
            )
          })}
          <button
            type='button'
            className='members-search-pagination__nav'
            onClick={() => goTo(page + 1)}
            disabled={page === totalPages}
            aria-label='Next page'
          >
            ›
          </button>
        </nav>
      )}
    </div>
  )
}

export {MembersSearchPage}
